import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useToast } from "../components/Toast";

const TABS = [
  {
    id: "appearance", label: "Appearance",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
      </svg>
    ),
  },
  {
    id: "notifications", label: "Notifications",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
  },
  {
    id: "security", label: "Security",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
  },
  {
    id: "preferences", label: "Preferences",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
      </svg>
    ),
  },
  {
    id: "data", label: "Data & Privacy",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582 4 8 4m8-4c0-2.21-3.582 4-8 4" />
      </svg>
    ),
  },
];

function Toggle({ checked, onChange, disabled = false }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none
        ${checked ? "bg-luxury-cta" : "bg-luxury-muted"}
        ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
          ${checked ? "translate-x-6" : "translate-x-1"}`}
      />
    </button>
  );
}

function SettingRow({ label, description, children }) {
  return (
    <div className="flex items-center justify-between py-4 border-b border-luxury-border last:border-0">
      <div className="flex-1 pr-6">
        <p className="text-sm font-medium text-luxury-text">{label}</p>
        {description && (
          <p className="text-xs mt-0.5 text-luxury-muted">{description}</p>
        )}
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  );
}

function SectionCard({ title, icon, children }) {
  return (
    <div className="rounded-2xl border border-luxury-border overflow-hidden bg-luxury-surface">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-luxury-border">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-luxury-elevated text-luxury-gold">
          {icon}
        </div>
        <h3 className="font-semibold text-sm text-luxury-text">{title}</h3>
      </div>
      <div className="px-5">{children}</div>
    </div>
  );
}

export default function Settings() {
  const { logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { addToast } = useToast();

  const [activeTab, setActiveTab] = useState("appearance");

  // Appearance
  const [compactMode, setCompactMode] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [fontSize, setFontSize] = useState("medium");

  // Notifications
  const [notifs, setNotifs] = useState({
    emailUpdates: true,
    documentAlerts: true,
    taxDeadlines: true,
    loginAlerts: true,
    weeklyReport: false,
    marketingEmails: false,
  });

  // Security
  const [twoFactor, setTwoFactor] = useState(false);
  const [sessionTimeout, setSessionTimeout] = useState("24h");
  const [loginHistory] = useState([
    { device: "Chrome — Windows", location: "Cape Town, ZA", time: "Today, 2:50 AM", current: true },
    { device: "Safari — iPhone", location: "Cape Town, ZA", time: "Yesterday, 9:14 PM", current: false },
    { device: "Chrome — Windows", location: "Cape Town, ZA", time: "18 Apr, 3:22 PM", current: false },
  ]);

  // Preferences
  const [prefs, setPrefs] = useState({
    currency: "ZAR",
    dateFormat: "DD/MM/YYYY",
    taxYearStart: "March",
    language: "English",
    autoSave: true,
    confirmDelete: true,
  });

  // Data
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const selectClass = "text-sm border border-luxury-border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-luxury-gold/30 bg-luxury-elevated text-luxury-text";

  const handleSaveNotifs = () => {
    addToast("Notification preferences saved!", "success");
  };

  const handleSavePrefs = () => {
    addToast("Preferences saved successfully!", "success");
  };

  const handleExportData = () => {
    addToast("Your data export has been requested. You'll receive an email shortly.", "success");
  };

  const handleDeleteAccount = () => {
    if (deleteConfirmText === "DELETE") {
      addToast("Account deletion request submitted.", "error");
      setShowDeleteAccount(false);
      setTimeout(() => logout(), 1500);
    } else {
      addToast("Please type DELETE to confirm.", "error");
    }
  };

  return (
    <div className="min-h-screen bg-luxury-bg">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">

        {/* Header */}
        <div className="rounded-2xl border border-luxury-border bg-luxury-surface p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-luxury-elevated border border-luxury-gold/30 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-luxury-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-luxury-text">Settings</h1>
              <p className="text-luxury-secondary text-sm mt-0.5">Manage your account, preferences and security</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="rounded-2xl border border-luxury-border bg-luxury-surface p-1.5 flex gap-1 flex-wrap">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all
                ${activeTab === tab.id
                  ? "bg-luxury-cta text-white"
                  : "text-luxury-secondary hover:text-luxury-text hover:bg-luxury-elevated"}`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── APPEARANCE ── */}
        {activeTab === "appearance" && (
          <div className="space-y-4">
            <SectionCard
              title="Theme & Display"
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              }
            >
              <SettingRow label="Dark Mode" description="Switch between dark and light theme">
                <Toggle checked={isDark} onChange={toggleTheme} />
              </SettingRow>
              <SettingRow label="Compact Mode" description="Reduce spacing for a denser layout">
                <Toggle checked={compactMode} onChange={setCompactMode} />
              </SettingRow>
              <SettingRow label="Collapsed Sidebar" description="Start with sidebar minimised">
                <Toggle checked={sidebarCollapsed} onChange={setSidebarCollapsed} />
              </SettingRow>
            </SectionCard>

            <SectionCard
              title="Font Size"
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                </svg>
              }
            >
              <div className="py-4">
                <div className="flex gap-3">
                  {["small", "medium", "large"].map((size) => (
                    <button
                      key={size}
                      onClick={() => { setFontSize(size); addToast(`Font size set to ${size}`, "success"); }}
                      className={`flex-1 py-2.5 rounded-xl border text-sm font-medium transition-all capitalize
                        ${fontSize === size
                          ? "bg-luxury-cta border-transparent text-white"
                          : "border-luxury-border text-luxury-secondary hover:border-luxury-gold/40 hover:text-luxury-text"}`}
                    >
                      {size === "small" ? "Small" : size === "medium" ? "Medium" : "Large"}
                    </button>
                  ))}
                </div>
              </div>
            </SectionCard>

            <SectionCard
              title="Accent Colour — Gold"
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
              }
            >
              <div className="py-4">
                <div className="flex gap-3 items-center">
                  {["D4AF37", "3B82F6", "10B981", "8B5CF6", "EF4444"].map((color) => (
                    <button
                      key={color}
                      onClick={() => color === "D4AF37" ? addToast("Gold is the active accent colour", "success") : addToast("Additional themes coming soon!", "success")}
                      className={`w-9 h-9 rounded-full transition-all hover:scale-110 relative
                        ${color !== "D4AF37" ? "opacity-30" : ""}`}
                      style={{ backgroundColor: `#${color}` }}
                    >
                      {color === "D4AF37" && (
                        <svg className="w-4 h-4 text-black absolute inset-0 m-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  ))}
                  <span className="text-xs ml-1 text-luxury-muted">More themes coming soon</span>
                </div>
              </div>
            </SectionCard>
          </div>
        )}

        {/* ── NOTIFICATIONS ── */}
        {activeTab === "notifications" && (
          <div className="space-y-4">
            <SectionCard
              title="Email Notifications"
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              }
            >
              <SettingRow label="Platform Updates" description="Get notified about new features and improvements">
                <Toggle checked={notifs.emailUpdates} onChange={(v) => setNotifs({ ...notifs, emailUpdates: v })} />
              </SettingRow>
              <SettingRow label="Document Alerts" description="Notifications when documents are processed or failed">
                <Toggle checked={notifs.documentAlerts} onChange={(v) => setNotifs({ ...notifs, documentAlerts: v })} />
              </SettingRow>
              <SettingRow label="Tax Deadline Reminders" description="SARS filing deadlines and important tax dates">
                <Toggle checked={notifs.taxDeadlines} onChange={(v) => setNotifs({ ...notifs, taxDeadlines: v })} />
              </SettingRow>
              <SettingRow label="Login Alerts" description="Email when a new device logs into your account">
                <Toggle checked={notifs.loginAlerts} onChange={(v) => setNotifs({ ...notifs, loginAlerts: v })} />
              </SettingRow>
              <SettingRow label="Weekly Summary Report" description="A weekly digest of your document activity">
                <Toggle checked={notifs.weeklyReport} onChange={(v) => setNotifs({ ...notifs, weeklyReport: v })} />
              </SettingRow>
              <SettingRow label="Marketing Emails" description="Tips, offers and news from TaxVault">
                <Toggle checked={notifs.marketingEmails} onChange={(v) => setNotifs({ ...notifs, marketingEmails: v })} />
              </SettingRow>
            </SectionCard>

            <div className="flex justify-end">
              <button
                onClick={handleSaveNotifs}
                className="px-6 py-2.5 bg-luxury-cta hover:bg-luxury-cta-hover text-white font-semibold text-sm rounded-xl transition-all"
              >
                Save Preferences
              </button>
            </div>
          </div>
        )}

        {/* ── SECURITY ── */}
        {activeTab === "security" && (
          <div className="space-y-4">
            <SectionCard
              title="Account Security"
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              }
            >
              <SettingRow
                label="Two-Factor Authentication"
                description="Add an extra layer of security to your account (coming soon)"
              >
                <Toggle checked={twoFactor} onChange={(v) => { setTwoFactor(v); addToast("2FA coming in next update!", "success"); }} />
              </SettingRow>
              <SettingRow label="Session Timeout" description="Automatically log out after inactivity">
                <select
                  value={sessionTimeout}
                  onChange={(e) => { setSessionTimeout(e.target.value); addToast("Session timeout updated!", "success"); }}
                  className={selectClass}
                >
                  <option value="1h">1 Hour</option>
                  <option value="8h">8 Hours</option>
                  <option value="24h">24 Hours</option>
                  <option value="7d">7 Days</option>
                  <option value="never">Never</option>
                </select>
              </SettingRow>
            </SectionCard>

            <SectionCard
              title="Recent Login Activity"
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              }
            >
              {loginHistory.map((session, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between py-4 border-b border-luxury-border last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center
                      ${session.current
                        ? "bg-luxury-cta/10 text-luxury-cta"
                        : "bg-luxury-elevated text-luxury-muted"}`}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-luxury-text">
                        {session.device}
                        {session.current && (
                          <span className="ml-2 text-xs bg-luxury-cta text-white px-1.5 py-0.5 rounded-full font-semibold">Current</span>
                        )}
                      </p>
                      <p className="text-xs text-luxury-secondary">{session.location} · {session.time}</p>
                    </div>
                  </div>
                  {!session.current && (
                    <button
                      onClick={() => addToast("Session revoked successfully.", "success")}
                      className="text-xs font-medium px-3 py-1.5 rounded-lg transition-all text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/20"
                    >
                      Revoke
                    </button>
                  )}
                </div>
              ))}
            </SectionCard>
          </div>
        )}

        {/* ── PREFERENCES ── */}
        {activeTab === "preferences" && (
          <div className="space-y-4">
            <SectionCard
              title="Regional Settings"
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            >
              {[
                { label: "Currency", key: "currency", options: ["ZAR", "USD", "EUR", "GBP"] },
                { label: "Date Format", key: "dateFormat", options: ["DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD"] },
                { label: "Tax Year Start", key: "taxYearStart", options: ["March", "January", "April", "July"] },
                { label: "Language", key: "language", options: ["English", "Afrikaans", "Zulu", "Xhosa"] },
              ].map((field) => (
                <SettingRow key={field.key} label={field.label}>
                  <select
                    value={prefs[field.key]}
                    onChange={(e) => setPrefs({ ...prefs, [field.key]: e.target.value })}
                    className={selectClass}
                  >
                    {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </SettingRow>
              ))}
            </SectionCard>

            <SectionCard
              title="Behaviour"
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              }
            >
              <SettingRow label="Auto-save Documents" description="Automatically save document changes">
                <Toggle checked={prefs.autoSave} onChange={(v) => setPrefs({ ...prefs, autoSave: v })} />
              </SettingRow>
              <SettingRow label="Confirm Before Delete" description="Show confirmation dialog before deleting documents">
                <Toggle checked={prefs.confirmDelete} onChange={(v) => setPrefs({ ...prefs, confirmDelete: v })} />
              </SettingRow>
            </SectionCard>

            <div className="flex justify-end">
              <button
                onClick={handleSavePrefs}
                className="px-6 py-2.5 bg-luxury-cta hover:bg-luxury-cta-hover text-white font-semibold text-sm rounded-xl transition-all"
              >
                Save Preferences
              </button>
            </div>
          </div>
        )}

        {/* ── DATA & PRIVACY ── */}
        {activeTab === "data" && (
          <div className="space-y-4">
            <SectionCard
              title="Your Data"
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              }
            >
              <SettingRow
                label="Export All Data"
                description="Download a copy of all your documents and account data as a ZIP file"
              >
                <button
                  onClick={handleExportData}
                  className="px-4 py-2 rounded-xl text-sm font-semibold border border-luxury-border text-luxury-secondary hover:border-luxury-gold/40 hover:text-luxury-gold transition-all"
                >
                  Export
                </button>
              </SettingRow>
              <SettingRow
                label="Clear Document Cache"
                description="Remove temporarily cached files to free up storage"
              >
                <button
                  onClick={() => addToast("Cache cleared successfully!", "success")}
                  className="px-4 py-2 rounded-xl text-sm font-semibold border border-luxury-border text-luxury-secondary hover:border-luxury-gold/40 hover:text-luxury-gold transition-all"
                >
                  Clear
                </button>
              </SettingRow>
            </SectionCard>

            <SectionCard
              title="Privacy"
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              }
            >
              <SettingRow
                label="Usage Analytics"
                description="Help improve TaxVault by sharing anonymous usage data"
              >
                <Toggle checked={true} onChange={() => addToast("Analytics preference updated.", "success")} />
              </SettingRow>
              <SettingRow
                label="Crash Reports"
                description="Automatically send crash reports to help fix bugs"
              >
                <Toggle checked={true} onChange={() => addToast("Crash report preference updated.", "success")} />
              </SettingRow>
            </SectionCard>

            {/* Danger Zone */}
            <div className="rounded-2xl border-2 border-red-200 dark:border-red-900/40 overflow-hidden bg-red-50 dark:bg-red-900/5">
              <div className="px-5 py-4 border-b border-red-200 dark:border-red-900/30">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <h3 className="font-semibold text-sm text-red-600 dark:text-red-400">Danger Zone</h3>
                </div>
              </div>
              <div className="px-5 py-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-luxury-text">Delete Account</p>
                  <p className="text-xs mt-0.5 text-luxury-secondary">
                    Permanently delete your account and all data. This cannot be undone.
                  </p>
                </div>
                <button
                  onClick={() => setShowDeleteAccount(true)}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl transition-all ml-6 flex-shrink-0"
                >
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Delete Account Modal */}
      {showDeleteAccount && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl border border-luxury-border bg-luxury-surface p-6 shadow-2xl">
            <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-base font-bold text-center mb-1 text-luxury-text">Delete Your Account?</h3>
            <p className="text-sm text-center mb-4 text-luxury-secondary">
              This will permanently delete all your documents and data. Type <strong className="text-luxury-text">DELETE</strong> to confirm.
            </p>
            <input
              type="text"
              placeholder="Type DELETE to confirm"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-luxury-border text-sm outline-none mb-4 focus:ring-2 focus:ring-red-500/30 bg-luxury-elevated text-luxury-text placeholder-luxury-muted"
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setShowDeleteAccount(false); setDeleteConfirmText(""); }}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-luxury-border text-luxury-secondary hover:bg-luxury-elevated transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-all"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
